// Global Instructions Rule Applied!
// Read shape of stmt__fund_checks / stmt__fund_group_checks (spec A3.14
// stage 3): whether the investor statements of a fund or a consolidated fund
// can be made for a period end. `reconciliation` has one entry per legal
// fund - the ledger's investments at cost against the investment records'
// cost held. `blocking` stops the statements; `warnings` do not.
//
// The backend twin is investor_statement_data_record.py (StatementChecksSchema);
// both are strict, so a key the read starts to emit is added to both first.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

const ReconciliationCompanySchema = z
  .object({
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    prog__ledger_cost: z.number(),
    prog__cost_held: z.number(),
    prog__difference: z.number()
  })
  .strict()

export const StatementReconciliationSchema = z
  .object({
    funds__id: z.string(),
    funds__name: z.string().nullish(),
    prog__ledger_cost: z.number(),
    prog__cost_held: z.number(),
    prog__difference: z.number(),
    prog__tolerance: z.number(),
    prog__reconciled: z.boolean(),
    // investment journals that carry no tranche, so no company
    prog__untranched_ledger_cost: z.number(),
    // the companies that differ; empty while the fund reconciles
    companies: z.array(ReconciliationCompanySchema)
  })
  .strict()

export type StatementReconciliation = z.infer<typeof StatementReconciliationSchema>

export const StatementCheckSchema = z
  .object({
    code: z.string(),
    // null on an entry about the consolidation itself
    funds__id: z.string().nullish(),
    funds__name: z.string().nullish(),
    message: z.string(),
    // what the check found; the keys depend on the check
    details: z.array(z.record(z.string(), z.unknown()))
  })
  .strict()

export type StatementCheck = z.infer<typeof StatementCheckSchema>

export const InvestorStatementChecksSchema = z
  .object({
    period_end: z.string(),
    // nothing blocks
    passed: z.boolean(),
    reconciliation: z.array(StatementReconciliationSchema),
    blocking: z.array(StatementCheckSchema),
    warnings: z.array(StatementCheckSchema)
  })
  .strict()

export type InvestorStatementChecks = z.infer<typeof InvestorStatementChecksSchema>

export function validateInvestorStatementChecks(data: unknown): InvestorStatementChecks {
  try {
    return validateWithZodSchema(InvestorStatementChecksSchema, data, 'investor statement checks')
  } catch (error) {
    throw new Error(`Investor statement checks validation failed: ${error}`)
  }
}
